import apiClient from './apiClient';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Prescription Tour API — Tour status, practice visit & cleanup
 *
 * Endpoints:
 *   GET    /api/organisation/member/tour-status       — Get tour status
 *   POST   /api/organisation/member/tour-status       — Update tour status
 *   GET    /api/organisation/practice/patient          — Get practice patient
 *   POST   /api/organisation/practice/visit            — Create practice visit
 *   DELETE /api/organisation/practice/cleanup           — Delete practice data
 * ═══════════════════════════════════════════════════════════════════════
 */

// ── Get Tour Status (session-cached) ─────────────────────────────────
// Tour status is static for a session — it only changes when the user
// completes the tour (the POST below). But several components fetch it on
// every mount (PracticeTourNudge, chart-type resolver, practice page),
// measured ~22 identical calls in a few minutes. Cache it once per session
// (single-flight + cached value, same pattern as charts/core/useChartType.js)
// and invalidate it only on updateTourStatus().
let _tourPromise = null;   // in-flight
let _tourCache = null;     // last successful payload
let _tourResolved = false; // distinguishes "not fetched" from "cached null-ish"

/** Drop the cached tour status (called after the user completes the tour). */
export const resetTourStatusCache = () => {
  _tourPromise = null;
  _tourCache = null;
  _tourResolved = false;
};

export const fetchTourStatus = async () => {
  if (_tourResolved) return _tourCache;
  if (_tourPromise) return _tourPromise;

  _tourPromise = apiClient
    .get('/api/organisation/member/tour-status')
    .then((response) => {
      _tourCache = response.data;
      _tourResolved = true;
      return _tourCache;
    })
    .catch((error) => {
      // Don't cache the failure — let the next call retry.
      console.error('[TourAPI] Error fetching tour status:', error);
      return { rx_tour_completed: false, role: 'UNKNOWN', specialization: null };
    })
    .finally(() => {
      _tourPromise = null;
    });

  return _tourPromise;
};

// ── Update Tour Status ───────────────────────────────────────────────

export const updateTourStatus = async (completed = true) => {
  try {
    const response = await apiClient.post(
      `/api/organisation/member/tour-status?completed=${completed}`
    );
    resetTourStatusCache(); // status changed → next read reflects it
    return response.data;
  } catch (error) {
    console.error('[TourAPI] Error updating tour status:', error);
    throw error;
  }
};

// ── Get Practice Patient ─────────────────────────────────────────────

export const fetchPracticePatient = async (specialization = null) => {
  try {
    const params = specialization ? `?specialization=${encodeURIComponent(specialization)}` : '';
    const response = await apiClient.get(`/api/organisation/practice/patient${params}`);
    return response.data;
  } catch (error) {
    console.error('[TourAPI] Error fetching practice patient:', error);
    return {
      patient: {
        name: 'Demo Patient',
        age: 35,
        sex: 'Male',
        complaint: 'Fever and body pain since 3 days',
      },
      specialization: 'GENERAL_PHYSICIAN',
    };
  }
};

// ── Create Practice Visit ────────────────────────────────────────────

export const createPracticeVisit = async (specialization = null) => {
  try {
    const response = await apiClient.post('/api/organisation/practice/visit', {
      specialization: specialization || undefined,
    });
    return response.data;
  } catch (error) {
    console.error('[TourAPI] Error creating practice visit:', error);
    throw error;
  }
};

// ── Cleanup Practice Visit ───────────────────────────────────────────

export const cleanupPracticeVisit = async (visitId, patientId) => {
  try {
    const response = await apiClient.delete('/api/organisation/practice/cleanup', {
      params: { visit_id: visitId, patient_id: patientId },
    });
    return response.data;
  } catch (error) {
    console.error('[TourAPI] Error cleaning up practice visit:', error);
    // Non-critical — don't throw, the data will be orphaned but that's ok
    return { success: false, error: error.message };
  }
};
